<template>
  <base-dialog
    :title="$t('page.refund.refundPayment')"
    :actionName="refundButton()"
    @action="doAction"
    :actionDisabled="isValid()"
    :max-width="dialogWidth"
    buttonColor="blue"
    :showCancel="isRetryingRefund ? undefined : $t('common.action.cancel')"
    :hideCloseButton="isRetryingRefund"
    @close="showDialog = false"
    :show="showDialog"
    :isLoading="isLoading"
  >
    <template #content>
      <v-alert
        type="info"
        variant="tonal"
        density="comfortable"
        border="start"
        class="mb-4 refund-info-alert"
      >
        {{ debitCheck() ? $t("page.refund.refundForDebit") : $t("page.refund.refundHint") }}
      </v-alert>

      <v-alert v-if="refundHint" type="info" variant="tonal" density="compact" class="mb-4">
        {{ refundHint }}
      </v-alert>

      <v-alert v-if="isRetryingRefund" type="info" variant="tonal" density="compact" class="mb-4">
        {{ $t("page.refund.retryingRefund") }}
      </v-alert>

      <v-alert
        v-if="refundItemModeUnavailableMessage"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ refundItemModeUnavailableMessage }}
      </v-alert>

      <v-card class="refund-summary-card" variant="outlined">
        <v-card-title class="text-subtitle-1 font-weight-medium">
          {{ $t("page.refund.refundDetails") }}
        </v-card-title>
        <v-card-text>
          <template v-if="shouldAutoCalculateRefund">
            <v-row>
              <v-col cols="12" md="4" class="pb-4">
                <p class="app-field-label refund-form-label">
                  {{ $t("page.refund.refundAmount") || "Refund Amount" }}
                </p>
                <div class="refund-amount-display" v-html="formattedRefundAmount" />
                <p class="text-caption text-medium-emphasis mt-1">
                  <span class="font-weight-medium text-high-emphasis">
                    {{ $t("page.refund.maximumRefund") || "Maximum refund" }}:
                  </span>
                  <span v-html="formattedMaxRefund" />
                </p>
                <p class="text-caption text-medium-emphasis mt-1">
                  <span class="font-weight-medium text-high-emphasis">
                    {{ $t("page.refund.selectedItemsTotal") || "Items selected" }}:
                  </span>
                  <span v-html="formattedCartRefund" />
                </p>
              </v-col>
              <v-col cols="12" md="4" class="pb-4">
                <p class="app-field-label refund-form-label">
                  {{ $t("page.refund.restockingFee") || "Restocking Fee" }}
                </p>
                <amount-input
                  v-model="restockingFee"
                  :options="{ currency: currencyCode }"
                  :rules="[(v: number | null) => (v ?? 0) >= 0 || '']"
                  :disabled="isRetryingRefund"
                  hide-details
                />
                <p class="text-caption text-medium-emphasis mt-1">
                  {{
                    $t("page.refund.restockingFeeHint") ||
                    "Automatically deducted from the refund total."
                  }}
                </p>
              </v-col>
              <v-col v-if="shouldShowTipRefundToggle" cols="12" md="4" class="pt-2">
                <v-switch
                  v-model="includeTipRefund"
                  :label="tipSwitchLabel"
                  inset
                  hide-details
                  color="primary"
                  :disabled="isRetryingRefund"
                />
                <p class="text-caption text-medium-emphasis mt-1" v-html="tipAmountDisplay" />
                <p class="text-caption text-medium-emphasis mt-1">
                  {{ tipSwitchHelper }}
                </p>
              </v-col>
              <v-col cols="12" md="6">
                <p class="app-field-label refund-form-label mb-1 d-flex align-center">
                  <span>{{ $t("page.refund.reason.reason") }}</span>
                  <span class="refund-form-required text-error font-weight-medium ml-1">
                    ({{ $t("errors.required") }})
                  </span>
                </p>
                <v-select
                  v-model="refundReasonSelected"
                  :items="refundReasons"
                  item-title="title"
                  item-value="value"
                  :placeholder="refundReasonPlaceholder"
                  :disabled="isRetryingRefund"
                />
                <v-text-field
                  v-if="refundReasonSelected"
                  :label="refundReasonRequest()"
                  v-model="refundReasonDetails"
                  density="compact"
                  :disabled="isRetryingRefund"
                />
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <!-- refund input -->
            <v-row class="mt-2" align="start">
              <v-col cols="12" md="6">
                <p class="app-field-label refund-form-label">
                  {{ $t("page.payment.amount") }}
                </p>
                <amount-input
                  v-model="amount"
                  :key="amountKey"
                  :rules="paymentRules"
                  :options="{
                    currency: useAccountStore().getDefaultCurrency,
                  }"
                  :disabled="isRetryingRefund"
                />
                <p class="text-caption text-medium-emphasis mt-1">
                  <span class="font-weight-medium text-high-emphasis">
                    {{ $t("page.refund.maximumRefund") }}:
                  </span>
                  <span v-html="formattedMaxRefund" />
                </p>
              </v-col>
            </v-row>

            <!-- refund reason select -->
            <v-row align="start">
              <v-col cols="12" md="6" class="py-0">
                <p class="app-field-label refund-form-label d-flex align-center">
                  <span>{{ $t("page.refund.reason.reason") }}</span>
                  <span class="refund-form-required text-error font-weight-medium ml-1">
                    ({{ $t("errors.required") }})
                  </span>
                </p>
                <v-select
                  v-model="refundReasonSelected"
                  :items="refundReasons"
                  item-title="title"
                  item-value="value"
                  :placeholder="refundReasonPlaceholder"
                  :disabled="isRetryingRefund"
                />
                <v-text-field
                  v-if="refundReasonSelected"
                  :label="refundReasonRequest()"
                  v-model="refundReasonDetails"
                  density="compact"
                  :disabled="isRetryingRefund"
                />
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <!-- warning message -->
      <p
        v-if="parseFloat(String(amount ?? 0)) > getMaxRefundableAmount()"
        align="center"
        class="text-body-2"
        style="color: red"
      >
        {{ $t("errors.refundExceed", [getMaxRefundableAmount()]) }}
      </p>

      <!-- debit refund -->
      <v-row lass="mt-6" no-gutters justify="start" align="center" v-if="debitCheck()">
        <v-col sm="4" md="6" class="pb-5 text-center">
          <v-card-subtitle>{{ $t("page.terminals.terminal") }}</v-card-subtitle>
        </v-col>
        <v-col sm="12" md="5">
          <v-select
            class="pt-3 mr-8"
            v-model="terminalSelect"
            :items="terminals"
            item-title="label"
            item-value="terminalId"
          />
        </v-col>
      </v-row>

      <!-- Clear Reader Button (visible only during debit refund processing) -->
      <v-row v-if="debitRefundInProgress && debitCheck()" justify="center" class="mt-2 mb-4">
        <v-btn
          color="warning"
          @click="clearReader"
          :disabled="!terminalSelect || isClearingReader"
          prepend-icon="mdi-refresh"
          :loading="isClearingReader"
        >
          {{ $t("page.refund.clearReader") }}
        </v-btn>
        <v-card-subtitle class="text-center mt-2">
          {{ $t("page.refund.clearReaderHint") }}
        </v-card-subtitle>
      </v-row>
      <!-- Cart Items Section -->
      <v-card v-if="hasCart" class="mt-4 mb-4 cart-items-card" variant="outlined">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-cart</v-icon>
          {{ $t("page.refund.returnItems") }}
        </v-card-title>
        <v-card-subtitle>{{ $t("page.refund.adjustReturnQuantities") }}</v-card-subtitle>

        <v-card-text>
          <v-table>
            <thead>
              <tr>
                <th>
                  <!-- Empty column for image -->
                </th>
                <th>{{ $t("inventory.productAndPrice") }}</th>
                <th class="text-end">{{ $t("page.refund.unitPriceWithTax") }}</th>
                <th class="text-center">
                  {{ $t("page.refund.returnQuantity") }}
                </th>
                <th v-if="showInventoryReturnColumn" class="text-center">
                  {{ $t("page.refund.returnStatus") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in cartItems" :key="index">
                <td>
                  <v-avatar size="40" :image="getDefaultImage(item)" />
                </td>
                <td>
                  <div class="d-flex flex-column product-info">
                    <span class="font-weight-medium">{{ getProductClassName(item) }}</span>
                    <span class="text-caption font-weight-medium text-truncate product-name">
                      {{ getProductName(item) }}
                    </span>
                    <span class="text-caption text-grey price-nickname">
                      {{ item.priceNickname }}
                    </span>
                  </div>
                </td>
                <td class="text-end">
                  <span
                    class="text-body-2 font-weight-medium"
                    v-html="formatUnitPricePlusTax(item)"
                  />
                </td>
                <td class="text-center">
                  <div class="d-flex align-center justify-center">
                    <v-btn
                      icon="mdi-minus"
                      variant="text"
                      :disabled="isRetryingRefund || item.returnQuantity <= 0"
                      @click="decrementReturnQuantity(index)"
                    ></v-btn>
                    <span class="mx-2">{{ item.returnQuantity }}</span>
                    <v-btn
                      icon="mdi-plus"
                      variant="text"
                      :disabled="isRetryingRefund || item.returnQuantity >= item.availableQuantity"
                      @click="incrementReturnQuantity(index)"
                    ></v-btn>
                  </div>
                  <div class="text-caption text-grey mt-1">
                    {{ $t("page.refund.maxQuantity") }}:
                    {{ item.availableQuantity }}
                    <span v-if="item.previouslyRefundedQuantity > 0" class="ml-1">
                      ({{ item.previouslyRefundedQuantity }}
                      {{ $t("page.refund.previouslyRefunded") }})
                    </span>
                  </div>
                </td>
                <td v-if="showInventoryReturnColumn">
                  <template v-if="inventoryReturnEnabled && item.usedInventory !== false">
                    <v-select
                      v-model="item.returnStatus"
                      :items="returnStatusOptions"
                      item-title="title"
                      item-value="value"
                      :disabled="isRetryingRefund || item.returnQuantity <= 0"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="return-status-select"
                      return-object
                      @update:model-value="updateReturnStatus(index, $event)"
                    >
                      <template v-slot:item="{ item, props }">
                        <v-list-item v-bind="props">
                          <template v-slot:prepend>
                            <v-icon :color="getReturnStatusColor(getReturnStatusItem(item).value)">
                              {{ getReturnStatusIcon(getReturnStatusItem(item).value) }}
                            </v-icon>
                          </template>
                          <template v-slot:title>
                            {{ getReturnStatusItem(item).title }}
                          </template>
                          <template v-slot:subtitle>
                            <span class="text-caption">{{
                              getReturnStatusItem(item).description
                            }}</span>
                          </template>
                        </v-list-item>
                      </template>

                      <template v-slot:selection="{ item }">
                        <div class="d-flex align-center">
                          <v-icon
                            :color="getReturnStatusColor(getReturnStatusItem(item).value)"
                            size="small"
                            class="mr-2"
                          >
                            {{ getReturnStatusIcon(getReturnStatusItem(item).value) }}
                          </v-icon>
                          <span>{{ getReturnStatusItem(item).title }}</span>
                        </div>
                      </template>
                    </v-select>
                  </template>
                  <template v-else>
                    <div class="text-disabled text-center">—</div>
                  </template>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import AmountInput from "@/shared/components/Common/AmountInput.vue";
import { useAccountStore } from "@/account/stores/account";
import { usePaymentStore } from "@/payments/stores/payments";
import {
  buildCustomRefundPreviewRequest,
  classifyRefundExecutionStatus,
  createRefundExecutionAttempt,
  getUnavailableRefundItemLabels,
  resolveRefundPaymentMethodType,
  resolveRefundStripeReaderId,
  scopeRefundPreviewToReader,
  shouldRetainRefundExecutionAttempt,
  supportsAuthoritativeRefundQuote,
} from "@/payments/utils/refund.utils";
import { useReaderStore } from "@/terminals/stores/readers";
import { useRefundStore } from "@/payments/stores/refund";
import { useProductsStore } from "@/catalog/stores/products";
import i18n from "@/app/plugins/i18n";
import { useValidation } from "@/shared/composables/validation";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { getAmount, getStripeAmount } from "@/shared/utils/amount.utils";
import { getFirestore, collection, query, where, onSnapshot, Timestamp } from "firebase/firestore";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import { hasFeatureAccess } from "@/shared/utils/feature.utils";
import { formatMoney } from "@/shared/services/util.service";
import { useDisplay } from "vuetify";
import type { PaymentCartItem } from "@/payments/types/payments";
import type { Reader } from "@/terminals/types/readers";
import type {
  ExecuteRefundRequest,
  RefundPreviewRequest,
  RefundPreviewResponse,
  RefundReturnStatus,
} from "@nectarpay/api-contracts/refund";
import type { RefundExecutionAttempt } from "@/payments/utils/refund.utils";

type RefundPayment = NonNullable<ReturnType<typeof usePaymentStore>["selectedPayment"]>;

const getPaymentLatestCharge = (paymentValue: RefundPayment | null) => {
  const charge = paymentValue?.latest_charge;
  return charge && typeof charge === "object" ? charge : undefined;
};

const emit = defineEmits(["updated"]);
const amountKey = ref(0);
const amount = ref<number>();
const refundedAmount = ref(0);
const showDialog = ref(false);
const refundReasonDetails = ref("");
const isLoading = ref(false);
const refundReasonSelected = ref("");
const { paymentRules } = useValidation();
const refundMethod = ref("");
const terminals = ref<Reader[]>([]);
const terminalSelect = ref("");
const payment = computed(() => usePaymentStore().selectedPayment);
const isZeroDollarPayment = computed(
  () =>
    (typeof payment.value?.id === "string" && payment.value.id.startsWith("pi_zero_")) ||
    payment.value?.metadata?.zeroDollar === "true",
);
const productStore = useProductsStore();
const debitRefundInProgress = ref(false);
const isClearingReader = ref(false);
const { lgAndUp, md } = useDisplay();
const dialogWidth = computed(() => {
  if (lgAndUp.value) {
    return "50%";
  }
  if (md.value) {
    return "90%";
  }
  return "100%";
});
const restockingFee = ref(0);
const includeTipRefund = ref(false);
const refundQuote = ref<RefundPreviewResponse | null>(null);
const refundExecutionAttempt = ref<RefundExecutionAttempt | null>(null);
const isRetryingRefund = computed(() => refundExecutionAttempt.value !== null);
const refundItemModeUnavailable = ref(false);
const refundItemModeUnavailableMessage = ref("");
const usesAuthoritativeRefundQuote = computed(() =>
  supportsAuthoritativeRefundQuote(payment.value),
);
let quoteRequestSequence = 0;
const currencyCode = computed(
  () => payment.value?.currency || useAccountStore().getDefaultCurrency || "usd",
);

const formatCurrencyValue = (value: number) => {
  return formatMoney({
    amount: Math.round((Number(value) || 0) * 100),
    currency: currencyCode.value,
  });
};

const tipAmountValue = computed(() => {
  const tipCents = payment.value?.amount_details?.tip?.amount;
  return typeof tipCents === "number" ? tipCents / 100 : 0;
});

const formattedTipAmount = computed(() => formatCurrencyValue(tipAmountValue.value));

const tipSwitchLabel = computed(() => i18n.global.t("page.refund.refundTip"));

const tipSwitchHelper = computed(() => i18n.global.t("page.refund.refundTipHint"));

const tipAmountDisplay = computed(() => {
  const label = i18n.global.t("page.refund.tipCollected");
  return `<span class="font-weight-medium text-high-emphasis">${label}</span>: ${formattedTipAmount.value}`;
});

const hasTip = computed(() => tipAmountValue.value > 0);

const restockingFeeAmount = computed(() =>
  Math.max(
    0,
    typeof restockingFee.value === "number"
      ? restockingFee.value
      : parseFloat(String(restockingFee.value)) || 0,
  ),
);

type ReturnStatusOption = {
  value: string;
  title: string;
  description: string;
};

// Shape of the computed cart item used by this dialog
interface RefundCartItem {
  priceId: string;
  productName: string;
  priceNickname: string;
  priceSku: string;
  productId?: string;
  quantity: number;
  availableQuantity: number;
  previouslyRefundedQuantity: number;
  price: number;
  currency: string;
  returnQuantity: number;
  returnStatus?: ReturnStatusOption;
  usedInventory?: boolean;
}

// A tax calculation line item attached to the payment (Stripe Tax shape)
interface TaxCalculationLineItem {
  reference?: string;
  quantity?: number;
  amount?: number;
  amount_tax?: number;
  tax_behavior?: string;
}

// Define interface for return item
interface ReturnItem {
  priceId: string;
  quantity: number;
  status?: ReturnStatusOption;
}

// Track return quantities and statuses
const returnItems = ref<Record<string, ReturnItem>>({});

const taxCalculationLineItems = computed<TaxCalculationLineItem[]>(() => {
  const items = (payment.value as { tax_calculation_line_items?: TaxCalculationLineItem[] } | null)
    ?.tax_calculation_line_items;
  if (Array.isArray(items)) {
    return items;
  }
  return [];
});

const taxCalculationLineItemsMap = computed<Record<string, TaxCalculationLineItem>>(() => {
  return taxCalculationLineItems.value.reduce<Record<string, TaxCalculationLineItem>>(
    (acc, entry) => {
      if (entry?.reference) {
        acc[entry.reference] = entry;
      }
      return acc;
    },
    {},
  );
});

const resolveCartEntry = (priceId: string) => {
  const cartMap = payment.value?.cart?.cartMap;
  if (!cartMap || Array.isArray(cartMap)) return undefined;
  return cartMap[priceId];
};

const resolveBaseUnitAmount = (cartEntry: PaymentCartItem | undefined) => {
  if (!cartEntry) return 0;
  return (
    cartEntry.unitAmount ??
    cartEntry.unit_amount ??
    cartEntry.price ??
    cartEntry.priceInfo?.unit_amount ??
    0
  );
};

const resolveNetUnitAmount = (cartEntry: PaymentCartItem | undefined) => {
  if (!cartEntry) return 0;
  const discounted =
    cartEntry.discountUnitAmount ??
    cartEntry.discount_unit_amount ??
    (typeof cartEntry.discountAmount === "number"
      ? Math.max(0, resolveBaseUnitAmount(cartEntry) - cartEntry.discountAmount)
      : typeof cartEntry.discount_amount === "number"
        ? Math.max(0, resolveBaseUnitAmount(cartEntry) - cartEntry.discount_amount)
        : null);
  if (typeof discounted === "number") {
    return discounted;
  }
  return resolveBaseUnitAmount(cartEntry);
};

const normalizeTaxBehavior = (behavior?: string | null) => {
  return (behavior || "").toString().toLowerCase();
};

const getUnitPricePlusTaxCents = (item: RefundCartItem): number => {
  const quotedLine = refundQuote.value?.lines.find((line) => line.cartKey === item.priceId);
  if (quotedLine) return quotedLine.unitRefundAmount;
  const cartEntry = resolveCartEntry(item.priceId);
  const taxEntry = taxCalculationLineItemsMap.value[item.priceId];

  const normalizePositiveNumber = (value: unknown) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
  };

  const quantity =
    normalizePositiveNumber(taxEntry?.quantity) ??
    normalizePositiveNumber(cartEntry?.quantity) ??
    normalizePositiveNumber(item?.quantity) ??
    1;

  if (!taxEntry) {
    return resolveNetUnitAmount(cartEntry);
  }

  const baseAmount =
    typeof taxEntry.amount === "number"
      ? taxEntry.amount / quantity
      : resolveNetUnitAmount(cartEntry);

  const taxAmount = typeof taxEntry.amount_tax === "number" ? taxEntry.amount_tax / quantity : 0;

  const taxBehavior =
    normalizeTaxBehavior(taxEntry.tax_behavior) ||
    normalizeTaxBehavior((cartEntry as { tax_behavior?: string } | undefined)?.tax_behavior) ||
    "exclusive";

  const unitTotal = taxBehavior === "inclusive" ? baseAmount : baseAmount + taxAmount;

  return Math.round(unitTotal);
};

const formatUnitPricePlusTax = (item: RefundCartItem) => {
  const cents = getUnitPricePlusTaxCents(item);
  return formatMoney({
    amount: typeof cents === "number" ? cents : 0,
    currency: item.currency || payment.value?.currency || useAccountStore().getDefaultCurrency,
  });
};

const refundReasons = [
  {
    value: "duplicate",
    title: i18n.global.t("page.refund.reason.duplicate"),
  },
  {
    value: "fraudulent",
    title: i18n.global.t("page.refund.reason.fraudulent"),
  },
  {
    value: "requested_by_customer",
    title: i18n.global.t("page.refund.reason.requestedByCustomer"),
  },
];

const refundReasonPlaceholder = computed(() => {
  const key = "page.refund.reason.selectReason";
  const hasTranslation = typeof i18n.global?.te === "function" && i18n.global.te(key);
  return hasTranslation ? i18n.global.t(key) : "Select refund reason";
});

const refundHint = computed(() => {
  return refundedAmount.value
    ? i18n.global.t("page.refund.refundPaymentHint", [
        refundedAmount.value,
        useAccountStore().getDefaultCurrency,
      ])
    : "";
});

// New function to calculate the maximum refundable amount
const getMaxRefundableAmount = () => {
  if (refundQuote.value) {
    return refundQuote.value.summary.remainingRefundableAmount / 100;
  }
  const maxAmount = payment.value?.amount || 0;
  const latestChargeRefunded = getPaymentLatestCharge(payment.value)?.amount_refunded ?? 0;
  return getAmount(Math.max(0, maxAmount - latestChargeRefunded));
};

const formattedMaxRefund = computed(() =>
  formatCurrencyValue(
    refundQuote.value
      ? refundQuote.value.summary.remainingRefundableAmount / 100
      : getMaxRefundableAmount(),
  ),
);
const formattedRefundAmount = computed(() =>
  formatCurrencyValue(
    refundQuote.value ? refundQuote.value.summary.totalRefundAmount / 100 : amount.value || 0,
  ),
);

const reset = () => {
  amount.value = 0;
  amountKey.value++;
  refundReasonDetails.value = "";
  refundReasonSelected.value = "";
  isLoading.value = false;
  showDialog.value = false;
  returnItems.value = {};
  isClearingReader.value = false;
  debitRefundInProgress.value = false;
  restockingFee.value = 0;
  includeTipRefund.value = false;
  refundQuote.value = null;
  refundExecutionAttempt.value = null;
  refundItemModeUnavailable.value = false;
  refundItemModeUnavailableMessage.value = "";
};

// Credit refund
const doRefund = async () => {
  isLoading.value = true;
  try {
    const paymentIntentId = payment.value?.id;
    if (isZeroDollarPayment.value) {
      await useRefundStore().zeroDollarReturn(paymentIntentId ?? "", {
        returnItems: getReturnItems(),
        reason: refundReasonSelected.value,
        metadata: { reason_details: refundReasonDetails.value },
      });
      useSnackBarStore().toastSuccess({ message: i18n.global.t("page.refund.refundSuccess") });
    } else if (!usesAuthoritativeRefundQuote.value) {
      await useRefundStore().refundPayment(paymentIntentId ?? "", getLegacyRefundPayload());
      useSnackBarStore().toastSuccess({ message: i18n.global.t("page.refund.refundSuccess") });
    } else {
      if (!refundExecutionAttempt.value) {
        clearTimeout(previewTimer);
        previewTimer = undefined;
        await requestRefundPreview();
        const quoteToken = refundQuote.value?.quoteToken;
        if (!quoteToken || !refundQuote.value?.canExecute) {
          throw new Error(
            refundQuote.value?.issues[0]?.message || "Refund preview is unavailable.",
          );
        }
        const request: ExecuteRefundRequest = {
          quoteToken,
          reason: refundReasonSelected.value as
            | "duplicate"
            | "fraudulent"
            | "requested_by_customer",
          ...(refundReasonDetails.value ? { reasonDetails: refundReasonDetails.value } : {}),
        };
        refundExecutionAttempt.value = createRefundExecutionAttempt(request);
      }

      const attempt = refundExecutionAttempt.value;
      const result = await useRefundStore().executeRefund(
        paymentIntentId ?? "",
        attempt.request,
        attempt.idempotencyKey,
      );
      const outcome = classifyRefundExecutionStatus(result.status);
      refundExecutionAttempt.value = null;
      if (outcome === "succeeded") {
        useSnackBarStore().toastSuccess({ message: i18n.global.t("page.refund.refundSuccess") });
      } else if (outcome === "pending") {
        useSnackBarStore().toastInfo({ message: i18n.global.t("page.refund.refundPending") });
      } else {
        useSnackBarStore().toastError({ message: i18n.global.t("page.refund.refundError") });
        isLoading.value = false;
        return;
      }
    }

    // Emit updated event to notify parent components
    emit("updated");

    // Close dialog and reset form
    showDialog.value = false;
    reset();
  } catch (error) {
    console.error(error);
    if (!shouldRetainRefundExecutionAttempt(error)) {
      refundExecutionAttempt.value = null;
    }
    useSnackBarStore().toastError({
      message: i18n.global.t("page.refund.refundError"),
    });
    isLoading.value = false;
  }
};

const open = async (id?: string) => {
  reset();
  showDialog.value = true;
  isLoading.value = true;
  if (id) {
    refundMethod.value = resolveRefundPaymentMethodType(payment.value);
    refundedAmount.value = getAmount(getPaymentLatestCharge(payment.value)?.amount_refunded ?? 0);
    // Set initial amount based on refund mode
    amount.value = shouldAutoCalculateRefund.value ? 0 : getMaxRefundableAmount();

    amountKey.value++;
    const accountTerminals = (await useReaderStore().listReaders(true, false)) || [];
    terminals.value = accountTerminals;
    terminalSelect.value = resolveRefundStripeReaderId(
      accountTerminals,
      payment.value?.metadata?.terminal,
    );
  }
  isLoading.value = false;
};

defineExpose({
  open,
});

const isValid = () => {
  const numericAmount = parseFloat(String(amount.value ?? ""));
  const exceedsMax = numericAmount > getMaxRefundableAmount();
  const invalidAmount = isNaN(numericAmount) || numericAmount <= 0;
  const hasReturnItems = Object.keys(getReturnItems()).length > 0;
  if (isZeroDollarPayment.value) {
    return !refundReasonSelected.value || !hasReturnItems;
  }
  if (!debitCheck()) {
    const quoteInvalid =
      usesAuthoritativeRefundQuote.value && refundQuote.value?.canExecute === false;
    return invalidAmount || !refundReasonSelected.value || exceedsMax || quoteInvalid;
  } else {
    return invalidAmount || !terminalSelect.value || !refundReasonSelected.value || exceedsMax;
  }
};

const refundButton = () => {
  const amountValue = amount.value ?? 0;
  const refundText = amount.value
    ? `${i18n.global.t("page.refund.refund")} $${amountValue.toFixed(2)}`
    : i18n.global.t("page.refund.refund");

  return refundText;
};

const refundReasonRequest = () => {
  const refundReason = refundReasonSelected.value as
    | "fraudulent"
    | "duplicate"
    | "requested_by_customer";
  const reasonMappings = {
    fraudulent: i18n.global.t("page.refund.reason.whyFraudulent"),
    duplicate: i18n.global.t("page.refund.reason.addMoreDetails"),
    requested_by_customer: i18n.global.t("page.refund.reason.addReason"),
  };

  return reasonMappings[refundReason] || i18n.global.t("page.refund.reason.addMoreDetails");
};

const debitCheck = () => {
  return refundMethod?.value === "interac_present";
};

//debit refund
const doDebitRefund = async () => {
  isLoading.value = true;
  debitRefundInProgress.value = true;
  let unsubscribeListener: (() => void) | null = null;
  const paymentIntentId = payment.value?.id;
  const terminalId = terminalSelect.value;

  // Timeout after 60 seconds
  const timeoutId = setTimeout(async () => {
    if (unsubscribeListener) {
      unsubscribeListener();
    }
    console.error("Debit refund listener timed out.");

    // Try to clear the reader automatically when timing out
    try {
      if (terminalId) {
        await useReaderStore().cancelTerminalAction(terminalId);
      }
    } catch (clearError) {
      console.error("Error clearing terminal after timeout:", clearError);
    }

    useSnackBarStore().toastError({
      message: i18n.global.t("page.refund.refundTimeout"),
    });
    isLoading.value = false;
    debitRefundInProgress.value = false;
  }, 60000);

  try {
    // Include return items metadata, similar to doRefund
    const returnItemsData = getReturnItems();
    const reason = refundReasonSelected.value;
    const reasonDetails = refundReasonDetails.value;
    const metadata = {
      reason,
      reason_details: reasonDetails,
    };

    const payload = {
      amount: getStripeAmount(amount.value ?? 0),
      returnItems: returnItemsData,
      reason,
      metadata,
    };

    // Initiate the refund process
    await useRefundStore().refundWithDebit(payload, paymentIntentId ?? "", terminalId);

    // Now, listen for the confirmation event in Firestore
    const db = getFirestore();
    const accountId = useAccountStore().getAccountId;
    const timelinesRef = collection(db, `accounts/${accountId}/timelines`);

    // Listen for the next successful debit refund, in the timeline for this payment
    const q = query(
      timelinesRef,
      where("pid", "==", paymentIntentId),
      where("isDebitRefund", "==", true),
      where("status", "==", "succeeded"),
      where("time", ">", Timestamp.now().seconds),
    );

    unsubscribeListener = onSnapshot(
      q,
      (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            // Clear the timeout
            clearTimeout(timeoutId);
            // Unsubscribe the listener
            if (unsubscribeListener) {
              unsubscribeListener();
              unsubscribeListener = null;
            }

            // Show success message
            useSnackBarStore().toastSuccess({
              message: i18n.global.t("page.refund.refundSuccess"),
            });

            // Emit updated event to notify parent components
            emit("updated");

            // Close dialog and reset form
            showDialog.value = false;
            reset();
            // Ensure isLoading is false here in case reset changes in future
            isLoading.value = false;
            debitRefundInProgress.value = false;
          }
        });
      },
      (error) => {
        console.error("Error listening for debit refund confirmation:", error);
        clearTimeout(timeoutId);
        if (unsubscribeListener) {
          unsubscribeListener();
        }
        useSnackBarStore().toastError({
          message: i18n.global.t("page.refund.listenerError"),
        });
        isLoading.value = false;
        debitRefundInProgress.value = false;
      },
    );
  } catch (error) {
    console.error("Error initiating debit refund:", error);
    clearTimeout(timeoutId);
    if (unsubscribeListener) {
      unsubscribeListener();
    }
    useSnackBarStore().toastError({
      message: i18n.global.t("page.refund.refundError"),
    });
    isLoading.value = false;
    debitRefundInProgress.value = false;
  }
};

// Add clear reader function
const clearReader = async () => {
  if (terminalSelect.value) {
    isClearingReader.value = true;

    // Set a timeout of 15 seconds for the API call
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error("Clearing reader timed out"));
      }, 15000);
    });

    try {
      // Race between the API call and the timeout
      await Promise.race([
        useReaderStore().cancelTerminalAction(terminalSelect.value),
        timeoutPromise,
      ]);

      useSnackBarStore().toastSuccess({
        message: i18n.global.t("page.refund.readerCleared") || "Reader cleared successfully",
      });

      // Stop the loading state and allow user to try again
      isLoading.value = false;
      debitRefundInProgress.value = false;
    } catch (error) {
      console.error("Error clearing terminal:", error);

      // If it was a timeout error, try to clear reader one more time silently
      if (error instanceof Error && error.message === "Clearing reader timed out") {
        try {
          // Try one more time silently
          await useReaderStore().cancelTerminalAction(terminalSelect.value);
          useSnackBarStore().toastSuccess({
            message:
              i18n.global.t("page.refund.readerClearedWithDelay") || "Reader cleared with delay",
          });
        } catch (retryError) {
          console.error("Error retrying clear terminal:", retryError);
          useSnackBarStore().toastError({
            message: i18n.global.t("page.refund.clearReaderError") || "Failed to clear reader",
          });
        }
      } else {
        useSnackBarStore().toastError({
          message: i18n.global.t("page.refund.clearReaderError") || "Failed to clear reader",
        });
      }

      // Regardless of what happened, stop the loading states
      isLoading.value = false;
      debitRefundInProgress.value = false;
    } finally {
      isClearingReader.value = false;
    }
  }
};

const doAction = async () => {
  try {
    if (!debitCheck()) {
      await doRefund();
    } else {
      await doDebitRefund();
    }
  } catch (error) {
    console.error("Error during refund process:", error);
    useSnackBarStore().toastError({
      message: i18n.global.t("page.refund.refundError"),
    });
    isLoading.value = false;
    debitRefundInProgress.value = false;
  }
};

// Return status options
const returnStatusOptions: ReturnStatusOption[] = [
  {
    value: "RESTOCKABLE",
    title: "Restockable",
    description: "The item is in good condition and can be immediately returned to inventory.",
  },
  {
    value: "ACTION_REQUIRED",
    title: "Action Required",
    description:
      "The item requires intervention—such as repair or refurbishment—before it can be restocked.",
  },
  {
    value: "NON_RESTOCKABLE",
    title: "Non-Restockable",
    description:
      "The item is deemed unsuitable for restocking, including those that need to be disposed of.",
  },
  {
    value: "PENDING_INSPECTION",
    title: "Pending Inspection",
    description: "The item's condition is yet to be determined and awaits further inspection.",
  },
];

const getReturnStatusItem = (item: ReturnStatusOption | { raw?: ReturnStatusOption }) =>
  ("raw" in item && item.raw ? item.raw : item) as ReturnStatusOption;

// Get color for return status
const getReturnStatusColor = (status: string) => {
  switch (status) {
    case "RESTOCKABLE":
      return "success";
    case "ACTION_REQUIRED":
      return "warning";
    case "NON_RESTOCKABLE":
      return "error";
    case "PENDING_INSPECTION":
      return "info";
    default:
      return "grey";
  }
};

// Get icon for return status
const getReturnStatusIcon = (status: string) => {
  switch (status) {
    case "RESTOCKABLE":
      return "mdi-check-circle";
    case "ACTION_REQUIRED":
      return "mdi-tools";
    case "NON_RESTOCKABLE":
      return "mdi-close-circle";
    case "PENDING_INSPECTION":
      return "mdi-magnify";
    default:
      return "mdi-help-circle";
  }
};

// Cart items
const hasCart = computed(() => {
  const cartMap = payment.value?.cart?.cartMap;
  return cartMap && Object.keys(cartMap).length > 0;
});

const inventoryReturnEnabled = computed(() =>
  isPaymentLinkInventory.value
    ? useAccountStore().account?.accountSettings?.inventory?.location_based_inventory_enabled ===
      true
    : hasFeatureAccess(PlatformFeature.INVENTORY),
);
const isPaymentLinkInventory = computed(() => {
  const paymentLinkPayment = payment.value?.paymentLinkPayment;
  return (
    payment.value?.cart?.paymentLinkInventory === true ||
    (typeof paymentLinkPayment === "object" && paymentLinkPayment?.paymentLinkInventory === true)
  );
});
const showInventoryReturnColumn = computed(() =>
  cartItems.value.some((item) => item.usedInventory !== false),
);

const shouldShowTipRefundToggle = computed(() => hasCart.value && hasTip.value);

const cartItems = computed<RefundCartItem[]>(() => {
  const rawCartMap = payment.value?.cart?.cartMap;
  if (!rawCartMap || Array.isArray(rawCartMap)) return [];

  return Object.entries(rawCartMap).map(([priceId, item]) => {
    // Get return data for this item if it exists in our current selection
    const returnData = returnItems.value[priceId];

    // Calculate previously refunded quantities for this item
    let previouslyRefundedQuantity = 0;

    // Check if there are previous refunds in the cart item's returnItems
    if (item.returnItems) {
      // Iterate through all return items for this cart item
      Object.values(item.returnItems).forEach((returnItem) => {
        if (returnItem && typeof returnItem.quantity === "number") {
          previouslyRefundedQuantity += returnItem.quantity;
        }
      });
    }

    // Calculate available quantity (original quantity minus previously refunded)
    const availableQuantity = Math.max(0, (item.quantity ?? 0) - previouslyRefundedQuantity);

    return {
      priceId,
      productName: item.productInfo?.name || "",
      priceNickname: item.priceInfo?.nickname || "",
      priceSku: (item.priceInfo as { sku?: string } | undefined)?.sku || "",
      productId: item.productId,
      quantity: item.quantity || 0,
      availableQuantity, // Add the available quantity after previous refunds
      previouslyRefundedQuantity, // Track how many were previously refunded
      price: item.price || 0,
      currency: item.currency || useAccountStore().getDefaultCurrency,
      returnQuantity: returnData?.quantity || 0,
      returnStatus:
        returnData?.status ?? (item.usedInventory === false ? undefined : returnStatusOptions[0]),
      usedInventory: item.usedInventory,
    };
  });
});

const shouldAutoCalculateRefund = computed(() => hasCart.value && !refundItemModeUnavailable.value);

const cartItemsMap = computed<Record<string, RefundCartItem>>(() => {
  return cartItems.value.reduce<Record<string, RefundCartItem>>((acc, item) => {
    acc[item.priceId] = item;
    return acc;
  }, {});
});

const calculatedCartRefundCents = computed(() => {
  if (refundQuote.value?.mode === "items") {
    return refundQuote.value.lines.reduce((sum, line) => sum + line.lineRefundAmount, 0);
  }
  if (!hasCart.value) return 0;
  return Object.values(returnItems.value).reduce((sum, selectedItem) => {
    if (!selectedItem || selectedItem.quantity <= 0) {
      return sum;
    }
    const cartItem = cartItemsMap.value[selectedItem.priceId] || {
      priceId: selectedItem.priceId,
    };
    const unitAmount = getUnitPricePlusTaxCents(cartItem);
    return sum + unitAmount * selectedItem.quantity;
  }, 0);
});

const calculatedCartRefund = computed(() => calculatedCartRefundCents.value / 100);

const tipRefundAmount = computed(() => {
  if (!shouldShowTipRefundToggle.value || !includeTipRefund.value) {
    return 0;
  }
  return tipAmountValue.value;
});

const cartRefundAfterRestocking = computed(() => {
  if (!shouldAutoCalculateRefund.value) return 0;
  const baseRefund = Math.max(0, calculatedCartRefund.value - restockingFeeAmount.value);
  return Math.max(0, baseRefund + tipRefundAmount.value);
});

const formattedCartRefund = computed(() => formatCurrencyValue(calculatedCartRefund.value));

const clampToMaxRefund = (value: number) => {
  const numeric = Number(value) || 0;
  const max = getMaxRefundableAmount();
  return Math.min(Math.max(numeric, 0), max);
};

watch(
  [cartRefundAfterRestocking, shouldAutoCalculateRefund],
  ([calculated, autoCalculate]) => {
    if (autoCalculate) {
      amount.value = clampToMaxRefund(calculated);
    }
  },
  { immediate: true },
);

watch(shouldShowTipRefundToggle, (show) => {
  if (!show) {
    includeTipRefund.value = false;
  }
});

const ensureReturnItemEntry = (item: { priceId: string; usedInventory?: boolean | null }) => {
  if (!returnItems.value[item.priceId]) {
    const base: ReturnItem = {
      priceId: item.priceId,
      quantity: 0,
    };

    if (item.usedInventory !== false) {
      base.status = returnStatusOptions[0];
    }

    returnItems.value[item.priceId] = base;
  }
};

const incrementReturnQuantity = (index: number) => {
  const item = cartItems.value[index];
  if (!item) return;

  ensureReturnItemEntry(item);

  // Use availableQuantity instead of quantity to respect previous refunds
  if (returnItems.value[item.priceId].quantity < item.availableQuantity) {
    returnItems.value[item.priceId].quantity++;
  }
};

const decrementReturnQuantity = (index: number) => {
  const item = cartItems.value[index];
  if (!item || !returnItems.value[item.priceId]) return;

  if (returnItems.value[item.priceId].quantity > 0) {
    returnItems.value[item.priceId].quantity--;
  }
};

// Update return status
const updateReturnStatus = (index: number, status: ReturnStatusOption) => {
  const item = cartItems.value[index];
  if (!item || item.usedInventory === false) return;

  ensureReturnItemEntry(item);
  returnItems.value[item.priceId].status = status;
};

// Include return items in refund metadata
interface RefundReturnItemPayload {
  productId?: string;
  quantity: number;
  sku?: string;
  returnStatus?: string;
}

const getLegacyRefundPayload = () => {
  const reason = refundReasonSelected.value;
  const metadata: Record<string, string> = {
    reason,
    reason_details: refundReasonDetails.value,
  };
  if (restockingFeeAmount.value > 0) {
    metadata.restocking_fee = restockingFeeAmount.value.toFixed(2);
  }
  const refundAmount = amount.value ?? 0;
  return {
    amount: getStripeAmount(refundAmount),
    returnItems: getReturnItems(),
    reason,
    metadata,
  };
};

const getSelectedRefundItems = (): Extract<RefundPreviewRequest, { mode: "items" }>["items"] => {
  const items: Extract<RefundPreviewRequest, { mode: "items" }>["items"] = {};
  Object.values(returnItems.value).forEach((item) => {
    if (item.quantity <= 0) return;
    const cartItem = cartItems.value.find((candidate) => candidate.priceId === item.priceId);
    if (!cartItem) return;
    const returnStatus = item.status?.value as RefundReturnStatus | undefined;
    items[item.priceId] = {
      quantity: item.quantity,
      disposition:
        cartItem.usedInventory === false
          ? { returnStatus: "NON_RESTOCKABLE" }
          : { returnStatus: returnStatus || "RESTOCKABLE" },
    };
  });
  return items;
};

const getRefundPreviewRequest = (): RefundPreviewRequest => {
  const items = getSelectedRefundItems();
  if (!shouldAutoCalculateRefund.value) {
    return scopeRefundPreviewToReader(
      buildCustomRefundPreviewRequest(getStripeAmount(amount.value ?? 0), items),
      terminalSelect.value,
    );
  }

  return scopeRefundPreviewToReader(
    {
      mode: "items",
      items,
      includeTip: includeTipRefund.value,
      restockingFeeAmount: getStripeAmount(restockingFeeAmount.value),
    },
    terminalSelect.value,
  );
};

const requestRefundPreview = async () => {
  if (
    !payment.value?.id ||
    isRetryingRefund.value ||
    debitCheck() ||
    isZeroDollarPayment.value ||
    !usesAuthoritativeRefundQuote.value
  )
    return;
  const sequence = ++quoteRequestSequence;
  try {
    const quote = await useRefundStore().previewRefund(payment.value.id, getRefundPreviewRequest());
    if (sequence !== quoteRequestSequence) return;
    refundQuote.value = quote;
    if (
      quote.mode === "items" &&
      quote.issues.some((issue) =>
        ["TAX_DATA_UNAVAILABLE", "ITEM_VALUE_UNAVAILABLE"].includes(issue.code),
      )
    ) {
      refundItemModeUnavailable.value = true;
      const itemLabels = getUnavailableRefundItemLabels(quote);
      refundItemModeUnavailableMessage.value = i18n.global.t("page.refund.itemModeUnavailable", {
        items: itemLabels.join(", "),
      });
      useSnackBarStore().toastWarning({ message: refundItemModeUnavailableMessage.value });
      return;
    }
    if (quote.mode === "items") {
      amount.value = quote.summary.totalRefundAmount / 100;
    }
  } catch (error) {
    if (sequence === quoteRequestSequence) refundQuote.value = null;
    throw error;
  }
};

let previewTimer: ReturnType<typeof setTimeout> | undefined;
watch(
  [returnItems, includeTipRefund, restockingFee, amount, shouldAutoCalculateRefund],
  () => {
    if (
      !showDialog.value ||
      isRetryingRefund.value ||
      debitCheck() ||
      isZeroDollarPayment.value ||
      !usesAuthoritativeRefundQuote.value
    )
      return;
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => void requestRefundPreview(), 250);
  },
  { deep: true },
);

const getReturnItems = () => {
  const result: Record<string, RefundReturnItemPayload> = {};

  if (!inventoryReturnEnabled.value && !isPaymentLinkInventory.value) {
    return result;
  }

  Object.values(returnItems.value).forEach((item) => {
    const cartItem = cartItems.value.find((ci) => ci.priceId === item.priceId);
    if (!cartItem || item.quantity <= 0) {
      return;
    }

    result[item.priceId] = {
      productId: cartItem.productId,
      quantity: item.quantity,
      sku: cartItem.priceSku,
    };

    if (cartItem.usedInventory !== false && item.status) {
      result[item.priceId].returnStatus = item.status.value;
    }
  });

  return result;
};

const resolveCartMapEntry = (priceId: string): PaymentCartItem | undefined => {
  const cartMap = payment.value?.cart?.cartMap;
  if (!cartMap || Array.isArray(cartMap)) return undefined;
  return cartMap[priceId];
};

const getDefaultImage = (item: RefundCartItem): string => {
  const currentItem = resolveCartMapEntry(item.priceId);

  const productImages = currentItem?.productInfo?.images;
  const productClass = currentItem?.productClassInfo;

  const productDefaultImage = productStore.getProductDefaultImage(productImages, productClass);
  return productDefaultImage;
};

const getProductName = (item: RefundCartItem): string => {
  const currentItem = resolveCartMapEntry(item.priceId);
  return currentItem?.productInfo?.name || "";
};

const getProductClassName = (item: RefundCartItem): string => {
  const currentItem = resolveCartMapEntry(item.priceId);
  return currentItem?.productClassInfo?.name || "";
};
</script>

<style scoped>
.return-status-select {
  max-width: 250px;
  margin: 0 auto;
}

.product-info {
  max-width: 240px;
}

.product-name {
  max-width: 100%;
  color: #1f1f1f;
}

.price-nickname {
  line-height: 1.2;
}

.refund-summary-card {
  border-radius: 12px;
  border-color: #d9d9d9;
}

.cart-items-card {
  border-color: #d9d9d9;
}

.refund-amount-display {
  font-size: 2rem;
  font-weight: 600;
  line-height: 1.2;
}

.summary-grid {
  gap: 12px;
}

.refund-form-label {
  margin-bottom: 4px;
  letter-spacing: 0.08em;
}

.refund-form-required {
  font-size: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
}

.refund-info-alert {
  border-left-width: 4px;
}
</style>
